'use client';

import { useCallback, useState } from 'react';
import { Layers, Check, Building2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useSettings } from '@/providers/settings-provider';
import { useEffectiveBackground } from '@/hooks/useEffectiveBackground';
import { useTranslation } from '@/hooks/useTranslation';
import { BackendAuth } from '@/lib/auth-backend';

const BG_OPTIONS = [
  {
    id: 'none',
    label: 'Kein',
    gradient: 'bg-gray-100 dark:bg-gray-800',
    dot: null,
  },
  {
    id: 'particles',
    label: 'Particle Mesh',
    gradient: 'bg-gradient-to-br from-cyan-500 to-violet-600',
    dot: '✦',
  },
  {
    id: 'aurora',
    label: 'Aurora',
    gradient: 'bg-gradient-to-br from-cyan-400 via-purple-500 to-emerald-400',
    dot: '◉',
  },
  {
    id: 'starfield',
    label: 'Starfield',
    gradient: 'bg-gradient-to-br from-slate-800 to-blue-950',
    dot: '★',
  },
  {
    id: 'orbs',
    label: 'Orbs',
    gradient: 'bg-gradient-to-br from-purple-500 to-blue-600',
    dot: '○',
  },
  {
    id: 'vortex',
    label: 'Vortex',
    gradient: 'bg-gradient-to-br from-cyan-500 via-violet-600 to-cyan-400',
    dot: '⊛',
  },
  {
    id: 'dna',
    label: 'DNA Helix',
    gradient: 'bg-gradient-to-br from-cyan-400 to-pink-500',
    dot: '⌘',
  },
  {
    id: 'crystals',
    label: 'Kristalle',
    gradient: 'bg-gradient-to-br from-violet-400 to-emerald-400',
    dot: '◈',
  },
  {
    id: 'hexgrid',
    label: 'Hex Grid',
    gradient: 'bg-gradient-to-br from-cyan-600 to-violet-700',
    dot: '⬡',
  },
  {
    id: 'grid',
    label: 'Grid Beams',
    gradient: 'bg-gradient-to-br from-slate-700 to-slate-900',
    dot: '▦',
  },
  {
    id: 'matrix',
    label: 'Matrix',
    gradient: 'bg-gradient-to-br from-black to-green-950',
    dot: '⌘',
  },
];

export function BackgroundPickerButton() {
  const { storeOption } = useSettings();
  const { t } = useTranslation();
  const { bgKey: current, own, orgDefault, themeCtx } = useEffectiveBackground();
  const [savingOrg, setSavingOrg] = useState(false);

  const select = useCallback((id) => {
    storeOption('ui.backgroundAnimation', id);
  }, [storeOption]);

  // Eigene Wahl verwerfen -> Firmen-Standard gilt wieder
  const useCompanyDefault = useCallback(() => {
    try { localStorage.removeItem('app_settings_ui.backgroundAnimation'); } catch {}
    storeOption('ui.backgroundAnimation', null);
    try { localStorage.removeItem('app_settings_ui.backgroundAnimation'); } catch {}
  }, [storeOption]);

  // Aktuelle Animation als Standard fuer die ganze Firma speichern
  // (organisation_themes.custom_variables.background_animation)
  const saveAsCompanyDefault = useCallback(async () => {
    const user = BackendAuth.getUser();
    const org = user?.organisation;
    if (!org) return;
    setSavingOrg(true);
    try {
      const existing = themeCtx?.theme?.advanced?.customVariables || {};
      const res = await BackendAuth.apiCallJson(`/api/theme/${org}`, {
        method: 'PUT',
        body: JSON.stringify({ custom_variables: { ...existing, background_animation: current } }),
      });
      if (res?.success) {
        try { localStorage.removeItem('app_settings_ui.backgroundAnimation'); } catch {}
        storeOption('ui.backgroundAnimation', null);
        try { localStorage.removeItem('app_settings_ui.backgroundAnimation'); } catch {}
        toast.success(t('background_picker.company_default_saved', { editable: false }));
        themeCtx?.fetchTheme?.();
      } else {
        toast.error(t('background_picker.company_default_failed', { editable: false }));
      }
    } catch (err) {
      toast.error(t('background_picker.company_default_failed', { editable: false }), { description: err?.message });
    } finally {
      setSavingOrg(false);
    }
  }, [current, storeOption, t, themeCtx]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          mode="icon"
          shape="circle"
          className="size-9 hover:bg-primary/10 hover:[&_svg]:text-primary"
          title="Hintergrund wählen"
        >
          <Layers className="size-4.5!" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        className="w-64 p-3"
      >
        <p className="text-xs font-semibold text-muted-foreground mb-2 px-0.5">
          Hintergrund-Animation
        </p>

        <div className="grid grid-cols-2 gap-1.5">
          {BG_OPTIONS.map((opt) => {
            const isActive = current === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => select(opt.id)}
                className={`
                  relative flex items-center gap-2 px-2.5 py-2 rounded-lg text-left
                  transition-all duration-150 group
                  ${isActive
                    ? 'bg-primary/10 ring-1 ring-primary/30'
                    : 'hover:bg-muted'}
                `}
              >
                {/* Color swatch */}
                <div className={`
                  shrink-0 w-7 h-7 rounded-md flex items-center justify-center
                  text-white text-sm font-bold select-none
                  ${opt.gradient}
                `}>
                  {opt.dot && <span className="text-[11px] leading-none">{opt.dot}</span>}
                </div>

                {/* Label */}
                <span className={`text-xs font-medium leading-tight flex-1 ${isActive ? 'text-primary' : 'text-foreground'}`}>
                  {opt.label}
                </span>

                {/* Active checkmark */}
                {isActive && (
                  <Check className="size-3 text-primary shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        {/* Firmen-Standard */}
        <div className="mt-3 border-t border-border pt-2.5 space-y-1.5">
          <p className="flex items-center gap-1.5 px-0.5 text-[11px] text-muted-foreground">
            <Building2 className="size-3" />
            {t('background_picker.company_default')}:{' '}
            <span className="font-medium text-foreground">
              {BG_OPTIONS.find((o) => o.id === orgDefault)?.label || t('background_picker.not_set')}
            </span>
          </p>
          {own && orgDefault && own !== orgDefault && (
            <Button variant="ghost" size="sm" className="h-7 w-full justify-start gap-1.5 text-xs" onClick={useCompanyDefault}>
              <RotateCcw className="size-3" />
              {t('background_picker.use_company_default')}
            </Button>
          )}
          {current !== orgDefault && (
            <Button variant="outline" size="sm" className="h-7 w-full justify-start gap-1.5 text-xs"
                    disabled={savingOrg} onClick={saveAsCompanyDefault}>
              <Building2 className="size-3" />
              {t('background_picker.set_company_default')}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
